import { JSX } from "react";
import "../../../../App.scss";
import Helper from "../../../../Common/Helper";
import { appContext } from "../../../../AppContext";
import { IbssPage } from "../../../../Components/Core/BasePage/IbssPage";
import { IRouterProps, withRouting } from "@iconics/components";
import { SerendieSymbol } from "@serendie/symbols";
import { Button, ButtonBar, Card, DataGrid, GridContainer, GridItem, IGridData, IGridRowsRequest, IGridRowsResponse, LayoutContainer, LayoutContent, RightButtons, TextField, TickSymbol } from "@iconics/components";
import { PagedResponse } from "@iconics/core";
import { NotificationData } from "./DataModels";
import { longDebounceTimeInMilliseconds } from "@iconics/core";
import { Guid } from "@iconics/core";

class ListNotifications extends IbssPage<IProps, IState>
{
    private get labels() { return appContext().labels; }
    private userCanCreate: boolean;
    private filterTextTimer = 0;

    constructor(props: IProps)
    {
        super(props);
        this.state = {
            filterText: '',
            notificationsGrid: { rows: [] },
            notificationsGridRefreshToken: Guid.new().toString(),
        };
        this.userCanCreate = appContext().localStorageProvider.hasRight('ADMINPORTAL.Notifications.Create');
    }

    public async componentDidMount(): Promise<void>
    {
        this.pageTitle = this.labels.Platform_Notification_PageTitle;
    }

    private onFilterTextChange(value: string): void 
    {
        this.setState({ filterText: value });
        window.clearTimeout(this.filterTextTimer);

        this.filterTextTimer = window.setTimeout(
            () => this.setState({ notificationsGridRefreshToken: Guid.new().toString() }),
            longDebounceTimeInMilliseconds,
        );
    }

    private handleAdd(): void
    {
        this.props.navigate('/notifications/0');
    }

    private handleEdit(notificationId: number): void
    {
        this.props.navigate("/notifications/" + notificationId);
    }

    private async handleRowsRequest(request: IGridRowsRequest<INotification>): Promise<IGridRowsResponse<INotification>>
    {
        const { filterText } = this.state;

        const response = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.notificationRules.get<PagedResponse<NotificationData[]>>({
            select: NotificationData,
            tenancy: appContext().tenantId,
            skipToken: request.skipToken,
            top: request.take,
            filter: `contains(${Helper.nameOf<NotificationData>('Name')}, '${filterText}') or contains(${Helper.nameOf<NotificationData>('EventTypeName')}, '${filterText}')`,
            orderBy: DataGrid.toODataOrderBy(request.sorter),
        });

        const notifications = response.value.map(i =>
        ({
            id: i.Id,
            name: i.Name,
            eventType: i.EventTypeName ?? '',
            shouldSendMail: i.ShouldSendMail,
            shouldSendMobilePush: i.ShouldSendMobilePush,
        }));

        return { rows: notifications, nextSkipToken: response.skipToken };
    }

    public render(): JSX.Element
    {
        return (
            <LayoutContainer>
                <LayoutContent>
                    <Card
                        icon={<SerendieSymbol name='bell' />}
                        title={this.labels.Platform_Notification_ListTitle}
                        subTitle={this.labels.Platform_Notification_ListSubTitle}
                    >
                        <GridContainer>
                            <GridItem expanded={3}>
                                <TextField
                                    leftContent={<SerendieSymbol name={"magnifying-glass"}/>}
                                    placeholder={this.labels.Platform_CommonAction_Search}
                                    value={this.state.filterText}
                                    onChange={e => this.onFilterTextChange(e.target.value)}
                                />
                            </GridItem>
                            <GridItem expanded={9}>
                                <ButtonBar>
                                    <RightButtons>
                                        <Button
                                            size='small'
                                            styleType='ghost'
                                            leftIcon={<SerendieSymbol name='plus' />}
                                            onClick={() => this.handleAdd()}
                                            disabled={!this.userCanCreate}
                                        >
                                            {this.labels.Platform_CommonAction_Add}
                                        </Button>
                                    </RightButtons>
                                </ButtonBar>
                            </GridItem>
                            <GridItem>
                                <DataGrid
                                    columns={[
                                        {
                                            columnId: Helper.nameOf<NotificationData>("Name"),
                                            title: this.labels.Notification_Name,
                                            getCellValue: i => i.name,
                                            columnWidth: 30,
                                            sortable: true,
                                        },
                                        {
                                            columnId: Helper.nameOf<NotificationData>("EventTypeName"),
                                            title: this.labels.Notification_EventTypeName,
                                            getCellValue: i => i.eventType,
                                            columnWidth: 30,
                                            sortable: true,
                                        },
                                        {
                                            columnId: Helper.nameOf<NotificationData>("ShouldSendMail"),
                                            title: this.labels.Notification_ShouldSendEmail,
                                            getCellValue: i => i.shouldSendMail,
                                            template: i => <TickSymbol value={i.value as boolean} />,
                                            columnWidth: 20,
                                            sortable: true,
                                        },
                                        {
                                            columnId: Helper.nameOf<NotificationData>("ShouldSendMobilePush"),
                                            title: this.labels.Notification_ShouldSendMobilePush,
                                            getCellValue: i => i.shouldSendMobilePush,
                                            template: i => <TickSymbol value={i.value as boolean} />,
                                            columnWidth: 20,
                                            sortable: true,
                                        },
                                    ]}
                                    data={this.state.notificationsGrid}
                                    onChange={i => this.setState({ notificationsGrid: i })}
                                    rowId={i => i.id.toString()}
                                    onRowsRequest={i => this.handleRowsRequest(i)}
                                    onCellClicked={i => this.handleEdit(i.id)}
                                    refreshToken={this.state.notificationsGridRefreshToken}
                                />
                            </GridItem>
                        </GridContainer>
                    </Card>
                </LayoutContent>
            </LayoutContainer>
        );
    }
}

export default withRouting(ListNotifications);

export interface IProps extends IRouterProps
{
}

export interface IState
{
    filterText: string;
    notificationsGrid: IGridData<INotification>;
    notificationsGridRefreshToken: string;
}

export interface INotification
{
    id: number;
    name: string;
    eventType: string;
    shouldSendMail: boolean;
    shouldSendMobilePush: boolean;
}
